import React, { useCallback, useEffect, useRef, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import MapView from "@/components/shared/MapView";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import {
  useLiveMonitoringFilters,
  LOCATION_HIERARCHY,
  INCIDENT_TYPE_FILTER_OPTIONS,
} from "@/hooks/useLiveMonitoringFilters";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import { useVehicleStateSocket } from "@/hooks/useVehicleStateSocket";
import api, { extractApiError } from "@/lib/api";
import {
  fetchIncidentStatusCounts,
  getBySeverityCount,
  getByStatusSeverityCount,
} from "@/lib/incidentStatusCounts";
import { Activity, Filter, Gauge, Loader2, MapPin, Radio, RotateCcw, Truck, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

const READ_KEYS = (obj, keys) => keys.find((k) => obj?.[k] !== undefined);
const WS_DEBUG = process.env.NODE_ENV !== "production";
const INCIDENT_STATUS_FILTER = new Set(["OPEN", "ACKNOWLEDGED", "RESPONDING"]);
const INCIDENT_SEVERITY_FILTER = new Set(["CRITICAL", "HIGH"]);
const INCIDENT_SEED_QUERY = "status=OPEN,ACKNOWLEDGED,RESPONDING&severity=CRITICAL,HIGH&page_size=50";

function wsDebugLog(...args) {
  if (!WS_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log("[LiveMonitoring:WS]", ...args);
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function extractCoords(payload) {
  if (!payload) return null;

  if (Array.isArray(payload.coords) && payload.coords.length >= 2) {
    const lat = toNumber(payload.coords[0]);
    const lng = toNumber(payload.coords[1]);
    if (lat !== null && lng !== null) return [lat, lng];
  }

  if (Array.isArray(payload.location?.coordinates) && payload.location.coordinates.length >= 2) {
    const lat = toNumber(payload.location.coordinates[0]);
    const lng = toNumber(payload.location.coordinates[1]);
    if (lat !== null && lng !== null) return [lat, lng];
  }

  // Supports incident payload shape: { location: { lat, lon } }
  const nestedLat = toNumber(payload.location?.lat ?? payload.location?.latitude);
  const nestedLng = toNumber(payload.location?.lon ?? payload.location?.lng ?? payload.location?.longitude);
  if (nestedLat !== null && nestedLng !== null) return [nestedLat, nestedLng];

  const latKey = READ_KEYS(payload, ["lat", "latitude"]);
  const lngKey = READ_KEYS(payload, ["lng", "lon", "long", "longitude"]);
  const lat = toNumber(latKey ? payload[latKey] : undefined);
  const lng = toNumber(lngKey ? payload[lngKey] : undefined);

  if (lat !== null && lng !== null) return [lat, lng];
  return null;
}

function extractVehicleId(payload) {
  if (!payload) return null;
  const key = READ_KEYS(payload, ["vehicle_id", "vehicleId"]);
  return key ? String(payload[key]) : null;
}

/**
 * Map a single /vehicles/bulk-state item to the internal vehicle shape.
 * All telemetry fields from the API are strings — parse before use.
 */
function normalizeBulkVehicle(item) {
  const lat = parseFloat(item.lat);
  const lon = parseFloat(item.lon);
  const speed = parseFloat(item.gps_speed);
  const obdSpeed = parseFloat(item.obd_speed);
  const rpm = parseFloat(item.rpm);
  const vehicleId = item.vehicle_id ?? item.vehicleId;
  return {
    id: vehicleId || item.registration_number || String(Date.now() + Math.random()),
    vehicle_id: vehicleId || null,
    plate: item.registration_number || "",
    registration_number: item.registration_number || "",
    make: item.make || "",
    model: [item.make, item.model].filter(Boolean).join(" ") || "Unknown",
    type: item.vehicle_category === "EMERGENCY_VEHICLE" ? "Emergency" : "User",
    vehicle_category: item.vehicle_category || "",
    device: item.device_id || item.device || null,
    owner: item.owner_name || item.driver_name || item.registration_number || "",
    status: item.status || "offline",
    coords: Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null,
    gps_speed: Number.isFinite(speed) ? speed : null,
    obd_speed: Number.isFinite(obdSpeed) ? obdSpeed : null,
    rpm: Number.isFinite(rpm) ? rpm : null,
    event: item.event || null,
    last_updated: item.last_updated || null,
    state: item.state || "",
    district: item.district || "",
    city: item.city || "",
    location: [item.city, item.district, item.state].filter(Boolean).join(", ") || "Unknown location",
  };
}

function toTitleCaseEvent(value) {
  const raw = String(value || "Unknown");
  return raw
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeIncidentForMap(item, index) {
  const lat = parseFloat(item?.location?.lat);
  const lon = parseFloat(item?.location?.lon);
  const coords = Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null;
  const primaryVehicle = Array.isArray(item?.vehicles) ? item.vehicles[0] : null;
  const statusRaw = String(item?.status || item?.current_status || "CLOSED").toUpperCase();
  const severityRaw = String(item?.severity || item?.priority || "high").toUpperCase();

  return {
    id: item?.id || item?.incident_id || `INC-${index + 1}`,
    incident_type: String(item?.incident_type || item?.type || item?.metadata?.event || "UNKNOWN").toUpperCase(),
    registration_number: primaryVehicle?.registration_number || "-",
    name: item?.driver?.name || "-",
    phone: item?.driver?.phone || "-",
    severity: ["CRITICAL", "HIGH", "MEDIUM", "LOW"].includes(severityRaw) ? severityRaw.toLowerCase() : "high",
    status: statusRaw,
    coords,
    timestamp: item?.event_time || item?.created_at || new Date().toISOString(),
    time: "now",
    services: item?.service_statuses || { police: "pending", ambulance: "pending", fire: "pending", hospital: "pending" },
  };
}

function isIncidentInScope(record) {
  const status = String(record?.status || "").toUpperCase();
  const severity = String(record?.severity || "").toUpperCase();
  return INCIDENT_STATUS_FILTER.has(status) && INCIDENT_SEVERITY_FILTER.has(severity);
}

export default function LiveMonitoring() {
  const [liveVehicles, setLiveVehicles] = useState([]);
  const [liveIncidents, setLiveIncidents] = useState([]);
  const [liveIncidentsError, setLiveIncidentsError] = useState("");
  const [incidentStatusCounts, setIncidentStatusCounts] = useState({ by_status: {}, by_status_severity: {}, total: 0 });
  const [liveTab, setLiveTab] = useState("map");
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedVehicleDetail, setSelectedVehicleDetail] = useState(null);
  const [selectedVehicleTelemetry, setSelectedVehicleTelemetry] = useState(null);
  const [vehicleDetailLoading, setVehicleDetailLoading] = useState(false);
  const [vehicleDetailError, setVehicleDetailError] = useState("");
  const [mapLoading, setMapLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const liveIncidentIdsRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const incidentDetailRequestRef = useRef(0);
  const vehicleByIdRef = useRef(new Map());
  const vehicleByPlateRef = useRef(new Map());
  const filtersRef = useRef(null);
  const wsVehicleStateLoggedRef = useRef(false);

  const { filters, setFilters, resetFilters, activeData } = useLiveMonitoringFilters(liveIncidents, liveVehicles);

  useEffect(() => {
    const vehicleIdMap = new Map();
    const plateMap = new Map();
    liveVehicles.forEach((v) => {
      const key = v.vehicle_id || v.id;
      if (key) vehicleIdMap.set(String(key), v);
      if (v.plate) plateMap.set(String(v.plate), v);
    });
    vehicleByIdRef.current = vehicleIdMap;
    vehicleByPlateRef.current = plateMap;
  }, [liveVehicles]);

  useEffect(() => {
    liveIncidentIdsRef.current = new Set(liveIncidents.map((item) => String(item.id)));
  }, [liveIncidents]);

  // ── /vehicles/bulk-state polling ──────────────────────────────────────────
  const fetchBulkVehicleState = useCallback(async () => {
    const f = filtersRef.current;
    try {
      const params = new URLSearchParams({ only_live: "true", page_size: 100 });
      if (f?.vehicleCategory && f.vehicleCategory !== "all") params.set("vehicle_category", f.vehicleCategory);
      if (f?.location?.state && f.location.state !== "all") params.set("state", f.location.state);
      if (f?.location?.district && f.location.district !== "all") params.set("district", f.location.district);
      if (f?.location?.city && f.location.city !== "all") params.set("city", f.location.city);

      const { data } = await api.get(`/vehicles/bulk-state?${params}`);
      const items = Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
      wsDebugLog("bulk-state response", { count: items.length, sample: items[0] });
      setLiveVehicles(items.map(normalizeBulkVehicle));
      setLastUpdatedAt(new Date());
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[LiveMonitoring] /vehicles/bulk-state failed", err);
      }
    } finally {
      setMapLoading(false);
    }
  }, []);

  const fetchSelectedVehicleDetail = useCallback(async (vehicleId) => {
    if (!vehicleId) return;

    setVehicleDetailLoading(true);
    setVehicleDetailError("");
    try {
      const [vehicleResponse, telemetryResponse] = await Promise.all([
        api.get(`/vehicles/${vehicleId}`),
        api.get(`/vehicles/${vehicleId}/telemetry/latest`),
      ]);

      setSelectedVehicleDetail(vehicleResponse?.data?.data || vehicleResponse?.data || null);
      setSelectedVehicleTelemetry(telemetryResponse?.data?.data || telemetryResponse?.data || null);
    } catch (err) {
      const { message } = extractApiError(err);
      setVehicleDetailError(message || "Failed to load vehicle details.");
      setSelectedVehicleTelemetry(null);
      setSelectedVehicleDetail(null);
    } finally {
      setVehicleDetailLoading(false);
    }
  }, []);

  const handleOpenVehicleDetail = useCallback((vehicleId) => {
    if (!vehicleId) return;
    setSelectedVehicleId(vehicleId);
    setVehicleDialogOpen(true);
  }, []);

  useEffect(() => {
    if (!vehicleDialogOpen || !selectedVehicleId) return;
    fetchSelectedVehicleDetail(selectedVehicleId);
  }, [fetchSelectedVehicleDetail, selectedVehicleId, vehicleDialogOpen]);

  // ── /incidents seed for map markers (helps when vehicles are offline/no GPS)
  const fetchIncidentsForMap = useCallback(async () => {
    const arraySerialized = api.getUri({
      url: "/incidents",
      params: {
        status: ["OPEN", "ACKNOWLEDGED", "RESPONDING"],
        severity: ["CRITICAL", "HIGH"],
        page_size: 50,
      },
    });
    const requestUrl = `/incidents?${INCIDENT_SEED_QUERY}`;
    wsDebugLog("incident seed query serialization", {
      arrayStyle: arraySerialized,
      literalStyle: requestUrl,
    });

    try {
      const response = await api.get(requestUrl);
      wsDebugLog("incident seed outgoing request", {
        url: response?.config?.url,
        baseURL: response?.config?.baseURL,
      });

      const { data } = response;
      const rows = Array.isArray(data) ? data : (data?.data ?? data?.results ?? data?.items ?? []);
      const mapped = Array.isArray(rows)
        ? rows.map(normalizeIncidentForMap).filter(isIncidentInScope)
        : [];
      setLiveIncidents(mapped);
      setLiveIncidentsError("");
    } catch (err) {
      const { message } = extractApiError(err);
      // Replace silent mock seed with an honest empty state and surface an inline error.
      setLiveIncidents([]);
      setLiveIncidentsError(message || "Failed to load incidents for map.");
      if (process.env.NODE_ENV !== "production") {
        console.warn("[LiveMonitoring] incident seed load failed", err);
      }
    }

    try {
      const countsResponse = await fetchIncidentStatusCounts();
      setIncidentStatusCounts(countsResponse);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[LiveMonitoring] status-counts load failed; keeping badge fallback state.", err);
      }
    }
  }, []);

  // Keep filtersRef current so the polling callback always reads latest filters
  useEffect(() => {
    filtersRef.current = filters;
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Seed on mount + re-seed when location filter changes; live updates are delivered via WebSocket
  useEffect(() => {
    fetchBulkVehicleState();
    fetchIncidentsForMap();
    // No polling intervals — real-time updates arrive via `useVehicleStateSocket` / `useIncidentSocket`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBulkVehicleState, fetchIncidentsForMap, filters.location.state, filters.location.district, filters.location.city]);

  useVehicleStateSocket((payload) => {
    if (!wsVehicleStateLoggedRef.current) {
      wsVehicleStateLoggedRef.current = true;
      wsDebugLog("vehicle_state sample", {
        payload,
        vehicle_id: payload?.vehicle_id,
        vehicleId: payload?.vehicleId,
      });
    }

    const vehicleId = extractVehicleId(payload);
    if (!vehicleId) return;

    // Filter by current UI filters using vehicle_id -> vehicle metadata lookup.
    const f = filtersRef.current || {};
    const idMap = vehicleByIdRef.current || new Map();
    const meta = idMap.get(String(vehicleId));

    // If any filter is active, require metadata to exist and match.
    const hasLocationFilter = f?.location && (f.location.state !== "all" || f.location.district !== "all" || f.location.city !== "all");
    const hasCategoryFilter = f?.vehicleCategory && f.vehicleCategory !== "all";

    if (hasLocationFilter || hasCategoryFilter) {
      if (!meta) return; // skip unknown vehicles
      if (hasCategoryFilter && String(meta.vehicle_category || meta.vehicleCategory || "").toLowerCase() !== String(f.vehicleCategory || "").toLowerCase()) return;
      if (hasLocationFilter) {
        if (f.location.state && f.location.state !== "all" && String((meta.state || "")).toLowerCase() !== String(f.location.state).toLowerCase()) return;
        if (f.location.district && f.location.district !== "all" && String((meta.district || "")).toLowerCase() !== String(f.location.district).toLowerCase()) return;
        if (f.location.city && f.location.city !== "all" && String((meta.city || "")).toLowerCase() !== String(f.location.city).toLowerCase()) return;
      }
    }

    const coords = extractCoords(payload);
    const speed = payload.gps_speed !== undefined ? parseFloat(payload.gps_speed) : undefined;
    const obdSpeed = payload.obd_speed !== undefined ? parseFloat(payload.obd_speed) : undefined;
    const rpm = payload.rpm !== undefined ? parseFloat(payload.rpm) : undefined;
    const statusKey = READ_KEYS(payload, ["status", "state"]);
    const status = statusKey ? String(payload[statusKey]).toLowerCase() : undefined;
    const event = payload.event ?? undefined;

    setLiveVehicles((prev) =>
      prev.map((v) => {
        const rowVehicleId = v.vehicle_id || v.id;
        if (String(rowVehicleId) !== String(vehicleId)) return v;
        return {
          ...v,
          ...(coords ? { coords } : {}),
          ...(status ? { status } : {}),
          ...(Number.isFinite(speed) ? { gps_speed: speed } : {}),
          ...(Number.isFinite(obdSpeed) ? { obd_speed: obdSpeed } : {}),
          ...(Number.isFinite(rpm) ? { rpm } : {}),
          ...(event !== undefined ? { event } : {}),
          last_updated: new Date().toISOString(),
        };
      })
    );

    const selectedId = String(selectedVehicleId || selectedVehicleDetail?.id || "");
    if (selectedVehicleDetail && selectedId === String(vehicleId)) {
      setSelectedVehicleTelemetry((prev) => ({
        ...(prev || {}),
        ...payload,
      }));
      setSelectedVehicleDetail((prev) =>
        prev
          ? {
              ...prev,
              status: status || prev.status,
              last_updated: payload.last_updated || new Date().toISOString(),
            }
          : prev
      );
    }
  });

  useIncidentSocket(async (payload) => {
    if (!isMountedRef.current) return;

    // Filter incidents by current location filters where possible
    const f = filtersRef.current || {};
    const hasLocationFilter = f?.location && (f.location.state !== "all" || f.location.district !== "all" || f.location.city !== "all");

    if (hasLocationFilter) {
      const plState = payload?.state || payload?.location?.state || payload?.location?.state_name || null;
      const plDistrict = payload?.district || payload?.location?.district || null;
      const plCity = payload?.city || payload?.location?.city || payload?.location?.town || null;

      // If any active location filter cannot be evaluated from payload, skip the incident
      if (f.location.state && f.location.state !== "all") {
        if (!plState || String(plState).toLowerCase() !== String(f.location.state).toLowerCase()) return;
      }
      if (f.location.district && f.location.district !== "all") {
        if (!plDistrict || String(plDistrict).toLowerCase() !== String(f.location.district).toLowerCase()) return;
      }
      if (f.location.city && f.location.city !== "all") {
        if (!plCity || String(plCity).toLowerCase() !== String(f.location.city).toLowerCase()) return;
      }
    }

    const vehicleId = extractVehicleId(payload);
    const regKey = READ_KEYS(payload, ["registration_number", "vehicle_number"]);
    const regNum = regKey ? payload[regKey] : null;
    const matchedVehicle = vehicleId
      ? vehicleByIdRef.current.get(String(vehicleId))
      : regNum
      ? vehicleByPlateRef.current.get(String(regNum))
      : null;
    const coords = extractCoords(payload) || matchedVehicle?.coords;
    const idKey = READ_KEYS(payload, ["incident_id", "incidentId", "id"]);
    const typeKey = READ_KEYS(payload, ["incident_type", "type", "title", "event"]);
    const severityKey = READ_KEYS(payload, ["severity", "level", "priority"]);

    const incidentId = idKey ? String(payload[idKey]) : null;
    if (!incidentId) return;
    const requestToken = ++incidentDetailRequestRef.current;

    const payloadStatusRaw = payload?.status ?? payload?.current_status;
    const payloadSeverityRaw = payload?.severity ?? payload?.priority ?? payload?.level;
    const payloadStatus = payloadStatusRaw !== undefined ? String(payloadStatusRaw).toUpperCase() : null;
    const payloadSeverity = payloadSeverityRaw !== undefined ? String(payloadSeverityRaw).toUpperCase() : null;

    if (payloadStatus && payloadSeverity) {
      const inScope = INCIDENT_STATUS_FILTER.has(payloadStatus) && INCIDENT_SEVERITY_FILTER.has(payloadSeverity);
      if (!inScope) {
        setLiveIncidents((prev) => prev.filter((row) => String(row.id) !== incidentId));
        return;
      }
    }

    const incidentExists = liveIncidentIdsRef.current.has(incidentId);
    let source = payload;

    // WS payloads are minimal; for newly seen incidents fetch full detail first
    // so status/vehicles/driver/service_statuses are complete before rendering.
    if (!incidentExists) {
      try {
        const detailResponse = await api.get(`/incidents/${incidentId}`);
        if (!isMountedRef.current || requestToken !== incidentDetailRequestRef.current) return;
        source = detailResponse?.data?.data || detailResponse?.data || payload;
      } catch {
        if (!isMountedRef.current || requestToken !== incidentDetailRequestRef.current) return;
        source = payload;
      }
    } else if (!payloadStatus || !payloadSeverity) {
      // Existing incident with minimal payload: refresh from REST so transitions
      // out of OPEN/ACKNOWLEDGED/RESPONDING or CRITICAL/HIGH are handled.
      try {
        const detailResponse = await api.get(`/incidents/${incidentId}`);
        if (!isMountedRef.current || requestToken !== incidentDetailRequestRef.current) return;
        source = detailResponse?.data?.data || detailResponse?.data || payload;
      } catch {
        if (!isMountedRef.current || requestToken !== incidentDetailRequestRef.current) return;
        source = payload;
      }
    }

    if (!isMountedRef.current || requestToken !== incidentDetailRequestRef.current) return;

    const normalizedSource = normalizeIncidentForMap(source, 0);
    if (!isIncidentInScope(normalizedSource)) {
      if (!isMountedRef.current || requestToken !== incidentDetailRequestRef.current) return;
      setLiveIncidents((prev) => prev.filter((row) => String(row.id) !== incidentId));
      return;
    }

    const incidentType = typeKey ? String(payload[typeKey]) : "Live Incident";
    const severityRaw = severityKey ? String(payload[severityKey]).toLowerCase() : "high";
    const severity = ["critical", "high", "medium", "low"].includes(severityRaw) ? severityRaw : "high";

    setLiveIncidents((prev) => {
      const incidentRow = {
        ...normalizedSource,
        // Preserve richer display hints derived from WS+vehicle lookup when present.
        incident_type: String(normalizedSource.incident_type || incidentType || "LIVE_INCIDENT").toUpperCase(),
        registration_number: normalizedSource.registration_number || matchedVehicle?.registration_number || matchedVehicle?.plate || regNum || "-",
        name: normalizedSource.name || payload?.driver?.name || matchedVehicle?.owner || "-",
        phone: normalizedSource.phone || payload?.driver?.phone || "-",
        severity: String(normalizedSource.severity || severity || "high").toLowerCase(),
        status: String(normalizedSource.status || payloadStatus || "OPEN").toUpperCase(),
        coords: normalizedSource.coords || coords,
        timestamp: normalizedSource.timestamp || new Date().toISOString(),
        time: normalizedSource.time || "now",
        services: normalizedSource.services || { police: "pending", ambulance: "pending", fire: "pending", hospital: "pending" },
      };

      const idx = prev.findIndex((x) => x.id === incidentId);
      if (idx === -1) return [incidentRow, ...prev].slice(0, 200);

      const next = [...prev];
      next[idx] = { ...next[idx], ...incidentRow };
      return next;
    });

    // The popup itself is raised once by TopBar (with full incident details).
  });

  const markers = activeData
    .filter((item) => item.coords) // ensure coords exist
    .map((item) => {
      if (item.dataType === "incident") {
        return {
          coords: item.coords,
          title: item.incident_type,
          subtitle: `${item.registration_number} · ${item.name} · ${item.phone}`,
          severity: item.severity,
          dataType: "incident",
          incidentId: item.id,
        };
      } else {
        return {
          coords: item.coords,
          title: item.plate,
          subtitle: `${item.model} · ${item.owner}`,
          severity: item.status === "alert" ? "critical" : item.status === "active" ? "low" : "medium",
          dataType: "vehicle",
          vehicleId: item.id,
          device: item.device,
          type: item.vehicle_category === "EMERGENCY_VEHICLE" ? "ambulance" : undefined,
        };
      }
    });

  const activeFiltersCount =
    ((filters.location.state !== "all" || filters.location.district !== "all" || filters.location.city !== "all") ? 1 : 0) +
    (filters.vehicleCategory !== "all" ? 1 : 0) +
    (filters.status !== "all" ? 1 : 0) +
    (filters.alertType !== "all" ? 1 : 0) +
    (filters.timeRange !== "all" ? 1 : 0);

  const selectedVehicleLiveRow = selectedVehicleId ? liveVehicles.find((vehicle) => String(vehicle.id) === String(selectedVehicleId)) : null;
  const selectedVehicleName = selectedVehicleDetail?.registration_number || selectedVehicleLiveRow?.plate || "Vehicle";
  const selectedVehicleDriver =
    selectedVehicleDetail?.driver?.name ||
    [selectedVehicleDetail?.driver?.first_name, selectedVehicleDetail?.driver?.last_name].filter(Boolean).join(" ").trim() ||
    selectedVehicleLiveRow?.owner ||
    "-";
  const selectedVehicleLocation = [
    selectedVehicleDetail?.city || selectedVehicleLiveRow?.city,
    selectedVehicleDetail?.district || selectedVehicleLiveRow?.district,
    selectedVehicleDetail?.state || selectedVehicleLiveRow?.state,
  ].filter(Boolean).join(", ") || "Unknown location";

  const handleMarkerClick = useCallback((marker) => {
    if (marker?.dataType !== "vehicle") return;
    handleOpenVehicleDetail(marker.vehicleId);
  }, [handleOpenVehicleDetail]);
  const navigate = useNavigate();
  const criticalIncidents = [...liveIncidents].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const byStatusSeverity = incidentStatusCounts?.by_status_severity || {};
  const openCritical = getByStatusSeverityCount(byStatusSeverity, "OPEN", "CRITICAL");
  const openHigh = getByStatusSeverityCount(byStatusSeverity, "OPEN", "HIGH");
  const criticalEventsBadgeCount = (openCritical || openHigh)
    ? openCritical + openHigh
    : getBySeverityCount(byStatusSeverity, "CRITICAL") + getBySeverityCount(byStatusSeverity, "HIGH");

  return (
    <div className="space-y-6" data-testid="live-monitoring">
      <PageHeader
        title="Live Monitoring"
        subtitle={`Live map · ${lastUpdatedAt ? `Updated ${lastUpdatedAt.toLocaleTimeString()}` : "Loading vehicles…"}`}
        actions={
          <div className="flex gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="relative" data-testid="filter-btn">
                  <Filter size={14} className="mr-1.5" /> Filter
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-900">Map Filters</h4>
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 px-2 text-slate-500 hover:text-slate-900">
                    <RotateCcw className="mr-2 h-3.5 w-3.5" />
                    Reset
                  </Button>
                </div>
                
                <div className="space-y-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Location Hierarchy</Label>
                  <div className="space-y-2">
                    <Select 
                      value={filters.location.state} 
                      onValueChange={(val) => setFilters(prev => ({
                        ...prev, 
                        location: { state: val, district: "all", city: "all" }
                      }))}
                    >
                      <SelectTrigger className="h-8 text-xs bg-white">
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {Object.keys(LOCATION_HIERARCHY).map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select 
                      value={filters.location.district} 
                      disabled={filters.location.state === "all"}
                      onValueChange={(val) => setFilters(prev => ({
                        ...prev, 
                        location: { ...prev.location, district: val, city: "all" }
                      }))}
                    >
                      <SelectTrigger className="h-8 text-xs bg-white">
                        <SelectValue placeholder="Select District" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Districts</SelectItem>
                        {filters.location.state !== "all" && Object.keys(LOCATION_HIERARCHY[filters.location.state]).map(district => (
                          <SelectItem key={district} value={district}>{district}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select 
                      value={filters.location.city} 
                      disabled={filters.location.district === "all"}
                      onValueChange={(val) => setFilters(prev => ({
                        ...prev, 
                        location: { ...prev.location, city: val }
                      }))}
                    >
                      <SelectTrigger className="h-8 text-xs bg-white">
                        <SelectValue placeholder="Select City" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Cities</SelectItem>
                        {filters.location.district !== "all" && LOCATION_HIERARCHY[filters.location.state][filters.location.district].map(city => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Vehicle Category</Label>
                  <Select value={filters.vehicleCategory} onValueChange={(val) => setFilters((prev) => ({ ...prev, vehicleCategory: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="EMERGENCY_VEHICLE">Emergency Vehicle</SelectItem>
                      <SelectItem value="PRIVATE">Private</SelectItem>
                      <SelectItem value="COMMERCIAL">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Alert Type</Label>
                  <Select value={filters.alertType} onValueChange={(val) => setFilters(prev => ({ ...prev, alertType: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select alert type" />
                    </SelectTrigger>
                    <SelectContent>
                      {INCIDENT_TYPE_FILTER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Vehicle Status</Label>
                  <Select value={filters.status} onValueChange={(val) => setFilters(prev => ({ ...prev, status: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="idle">Idle</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                      <SelectItem value="alert">Alert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Time Range</Label>
                  <Select value={filters.timeRange} onValueChange={(val) => setFilters(prev => ({ ...prev, timeRange: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Live / All Time</SelectItem>
                      <SelectItem value="24h">Last 24 Hours</SelectItem>
                      <SelectItem value="7d">Last 7 Days</SelectItem>
                      <SelectItem value="30d">Last 30 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
      />

      <Tabs value={liveTab} onValueChange={setLiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="map">Map</TabsTrigger>
          <TabsTrigger value="vehicles">
            Live Vehicles
            {liveVehicles.length > 0 && (
              <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-bold text-white">
                {liveVehicles.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="critical">
            Critical Events
            {criticalEventsBadgeCount > 0 && (
              <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">
                {criticalEventsBadgeCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Map tab ────────────────────────────────────────────────────── */}
        <TabsContent value="map">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3 fix-hei pb-10">
            <div className="xl:col-span-2 ">
              <SectionCard padded={false}>
                <div className="relative p-3">
              {mapLoading && (
                <div className="absolute inset-3 z-10 flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    <p className="text-xs font-medium text-slate-500">Loading vehicles…</p>
                  </div>
                </div>
              )}
              <MapView markers={markers} height={560} testId="live-map" onMarkerClick={handleMarkerClick} />
            </div>
              </SectionCard>
            </div>
            <SectionCard title="Live Events & Vehicles" padded={false}>
          {liveIncidentsError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {liveIncidentsError}
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {activeData.slice(0, 8).map((row) => (
              <li
                key={`${row.dataType}-${row.id || row.plate}`}
                className={`px-5 py-4 ${row.dataType === "vehicle" ? "cursor-pointer hover:bg-slate-50" : ""}`}
                onClick={() => {
                  if (row.dataType === "vehicle") {
                    handleOpenVehicleDetail(row.id);
                    return;
                  }
                  // Navigate to incidents page and open dialog for this incident
                  navigate("/admin/incidents", { state: { selectedId: row.id } });
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono text-xs font-semibold text-slate-900">
                    {row.dataType === "incident" ? row.incident_type : row.plate || row.id}
                  </p>
                  {row.dataType === "incident" ? (
                    <div className="flex items-center gap-2">
                      <SeverityBadge severity={row.severity} />
                      <StatusBadge status={row.status} />
                    </div>
                  ) : (
                    <StatusBadge status={row.status} />
                  )}
                </div>
                {row.dataType === "incident" ? (
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <p>Registration: <span className="font-semibold text-slate-900">{row.registration_number}</span></p>
                    <p>Name: <span className="font-semibold text-slate-900">{row.name}</span></p>
                    <p>Phone: <span className="font-semibold text-slate-900">{row.phone}</span></p>
                  </div>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-slate-700">{`${row.plate} · ${row.model}`}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{`Owner ${row.owner}`}</p>
                  </>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
          </div>
        </TabsContent>

        {/* ── Live Vehicles tab ──────────────────────────────────────────── */}
        <TabsContent value="vehicles">
          <SectionCard padded={false}>
            {liveVehicles.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
                {mapLoading
                  ? <><Loader2 className="h-8 w-8 animate-spin" /><p className="text-sm font-medium">Loading vehicles…</p></>
                  : <><Truck className="h-8 w-8" /><p className="text-sm font-medium">No live vehicles match the current filters.</p></>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="live-vehicles-table">
                  <thead>
                    <tr className="border-b border-slate-100 text-left">
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Registration</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Model</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Type</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Owner</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">GPS km/h</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">OBD km/h</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">RPM</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Event</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Location</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Last update</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {liveVehicles.map((v) => (
                      <tr
                        key={v.vehicle_id || v.id}
                        className="cursor-pointer transition hover:bg-slate-50/70"
                        onClick={() => handleOpenVehicleDetail(v.vehicle_id || v.id)}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{v.registration_number || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{v.model || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            v.vehicle_category === "EMERGENCY_VEHICLE"
                              ? "bg-rose-50 text-rose-700"
                              : "bg-slate-100 text-slate-600"
                          }`}>
                            {v.type || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{v.owner || "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                        <td className="px-4 py-3 text-slate-900">
                          {Number.isFinite(Number(v.gps_speed)) ? `${Number(v.gps_speed).toFixed(1)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {Number.isFinite(Number(v.obd_speed)) ? `${Number(v.obd_speed).toFixed(1)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {Number.isFinite(Number(v.rpm)) ? `${Math.round(Number(v.rpm))}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{v.event ? toTitleCaseEvent(v.event) : "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{v.location || "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {v.last_updated ? new Date(v.last_updated).toLocaleTimeString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* ── Critical Events tab ────────────────────────────────────────── */}
        <TabsContent value="critical">
          <SectionCard padded={false}>
            {criticalIncidents.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
                {mapLoading
                  ? <><Loader2 className="h-8 w-8 animate-spin" /><p className="text-sm font-medium">Loading critical events…</p></>
                  : <><Activity className="h-8 w-8" /><p className="text-sm font-medium">No matching critical events.</p></>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="critical-events-table">
                  <thead>
                    <tr className="border-b border-slate-100 text-left">
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Type</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Severity</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Registration</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Driver</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Phone</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {criticalIncidents.map((incident) => (
                      <tr
                        key={incident.id}
                        className="cursor-pointer transition hover:bg-slate-50/70"
                        onClick={() => navigate("/admin/incidents", { state: { selectedId: incident.id } })}
                      >
                        <td className="px-4 py-3 text-slate-700">{incident.incident_type || "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={String(incident.status || "").toLowerCase()} /></td>
                        <td className="px-4 py-3"><SeverityBadge severity={incident.severity || "high"} /></td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{incident.registration_number || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{incident.name || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{incident.phone || "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{incident.timestamp ? new Date(incident.timestamp).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>
      <br /><br /><br />
      <Dialog open={vehicleDialogOpen} onOpenChange={(open) => {
        setVehicleDialogOpen(open);
        if (!open) {
          setSelectedVehicleId(null);
          setSelectedVehicleDetail(null);
          setSelectedVehicleTelemetry(null);
          setVehicleDetailError("");
        }
      }}>
        <DialogContent className="sm:max-w-3xl rounded-[28px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">{selectedVehicleName}</DialogTitle>
            <DialogDescription>
              Live vehicle detail panel seeded from `/vehicles/{'{'}vehicle_id{'}'}` and `/vehicles/{'{'}vehicle_id{'}'}/telemetry/latest`.
            </DialogDescription>
          </DialogHeader>

          {vehicleDetailLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 size={20} className="mr-2 animate-spin" /> Loading vehicle detail...
            </div>
          ) : vehicleDetailError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {vehicleDetailError}
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Vehicle</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900 flex items-center gap-2"><Truck size={14} /> {selectedVehicleName}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Driver</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900 flex items-center gap-2"><User size={14} /> {selectedVehicleDriver}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Status</p>
                  <div className="mt-2"><StatusBadge status={selectedVehicleDetail?.status || selectedVehicleLiveRow?.status || "offline"} /></div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Live Speed</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900 flex items-center gap-2"><Gauge size={14} /> {toNumber(selectedVehicleTelemetry?.gps_speed ?? selectedVehicleTelemetry?.speed ?? selectedVehicleLiveRow?.gps_speed) ?? 0} km/h</p>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_360px]">
                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <MapView
                    markers={selectedVehicleLiveRow?.coords ? [{
                      coords: selectedVehicleLiveRow.coords,
                      title: selectedVehicleName,
                      subtitle: selectedVehicleLocation,
                      severity: selectedVehicleDetail?.status === "alert" ? "critical" : "low",
                      type: selectedVehicleDetail?.vehicle_category === "EMERGENCY_VEHICLE" ? "ambulance" : undefined,
                    }] : []}
                    center={selectedVehicleLiveRow?.coords || [19.076, 72.8777]}
                    zoom={14}
                    height={320}
                    testId="live-monitoring-vehicle-detail-map"
                  />
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Static Details</p>
                    <div className="mt-3 space-y-3 text-sm">
                      <p className="flex items-center gap-2 text-slate-700"><Truck size={14} /> {(selectedVehicleDetail?.make || "-")} {(selectedVehicleDetail?.model || "")}</p>
                      <p className="flex items-center gap-2 text-slate-700"><MapPin size={14} /> {selectedVehicleLocation}</p>
                      <p className="flex items-center gap-2 text-slate-700"><Activity size={14} /> Org: {selectedVehicleDetail?.org_name || selectedVehicleDetail?.organization?.name || "-"}</p>
                      <p className="flex items-center gap-2 text-slate-700"><Radio size={14} /> Registration: {selectedVehicleDetail?.registration_number || selectedVehicleLiveRow?.registration_number || selectedVehicleLiveRow?.plate || "-"}</p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Latest Telemetry</p>
                    <div className="mt-3 space-y-3 text-sm">
                      <p className="text-slate-700">Speed: <span className="font-semibold text-slate-900">{toNumber(selectedVehicleTelemetry?.gps_speed ?? selectedVehicleTelemetry?.speed ?? selectedVehicleLiveRow?.gps_speed) ?? 0} km/h</span></p>
                      <p className="text-slate-700">Event: <span className="font-semibold text-slate-900">{selectedVehicleTelemetry?.event || selectedVehicleLiveRow?.event || "-"}</span></p>
                      <p className="text-slate-700">Last updated: <span className="font-semibold text-slate-900">{selectedVehicleTelemetry?.last_updated || selectedVehicleDetail?.last_updated || selectedVehicleLiveRow?.last_updated ? new Date(selectedVehicleTelemetry?.last_updated || selectedVehicleDetail?.last_updated || selectedVehicleLiveRow?.last_updated).toLocaleString() : "-"}</span></p>
                      <p className="text-slate-700">Last trip: <span className="font-semibold text-slate-900">{selectedVehicleDetail?.last_trip ? new Date(selectedVehicleDetail.last_trip).toLocaleString() : "-"}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
